/**
 * EmailIntentPrompt — "which inbox should prest0n connect?" (2026-08-20
 * connect-journey batch).
 *
 * Shown BEFORE the OAuth window for email toolkits: identity cannot be
 * pinned during sign-in (no provider enforces a hint), so the journey
 * captures the INTENDED inbox first, pins the coach to it, and verifies who
 * actually signed in the moment the connection is ACTIVE — the incident this
 * closes bound an org's Outlook to a mailbox-less admin identity because the
 * tenant admin signed in herself during an approval interrupt.
 *
 * The address is optional — [skip] connects exactly as before. For Microsoft
 * the prompt carries the one warning that prevents the incident: an admin
 * should approve the app from their admin console, never sign in with their
 * own account.
 */

import React, { useEffect, useRef, useState } from 'react';
import PanelModal from './PanelModal';
import panelTheme from '../panelTheme';
import { checkTenantGrant } from '../ComposioClient';

export interface EmailIntentPromptProps {
  slug: string;
  displayName: string;
  onContinue: (intendedAddress: string | null) => void;
  onCancel: () => void;
}

export const EmailIntentPrompt: React.FC<EmailIntentPromptProps> = ({
  slug,
  displayName,
  onContinue,
  onCancel,
}) => {
  const [value, setValue] = useState('');
  // Firm-wide-approval memory: once someone at this firm got through the
  // admin wall, the NEXT person gets reassurance instead of the warning.
  // null = unknown (no address yet, lookup pending, or lookup failed).
  const [firmApproved, setFirmApproved] = useState<boolean | null>(null);
  const grantLookupSeq = useRef(0);
  const trimmed = value.trim();
  const valid = trimmed.length === 0 || (trimmed.includes('@') && trimmed.length <= 120);

  useEffect(() => {
    const seq = ++grantLookupSeq.current;
    if (!trimmed.includes('@') || trimmed.length > 120) {
      setFirmApproved(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      void checkTenantGrant(slug, trimmed)
        .then((granted) => {
          if (grantLookupSeq.current === seq) setFirmApproved(granted === true ? true : null);
        })
        .catch(() => { /* unknown stays unknown — the warning stands */ });
    }, 450);
    return () => clearTimeout(timer);
  }, [slug, trimmed]);

  const submit = () => {
    if (!valid) return;
    onContinue(trimmed.includes('@') ? trimmed : null);
  };

  return (
    <PanelModal onClose={onCancel} ariaLabel={`Which inbox for ${displayName}`} testId="email-intent-prompt">
      {/* No textTransform here: "Prest0n" is a NAME and keeps its capital P
          (2026-08-22 owner directive) — the string is authored in house
          lowercase directly. Spacing follows the SetupFieldsDialog rhythm;
          this dialog used to stack its blocks with zero margins. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.45rem' }}>
        <div style={{ fontWeight: 700, color: panelTheme.ink, fontSize: '1rem' }}>
          which inbox should Prest0n connect?
        </div>
        <button
          onClick={onCancel}
          aria-label="close"
          data-testid="email-intent-close"
          style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: panelTheme.grayFaint, fontSize: '1.1rem', lineHeight: 1, padding: 2,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ fontSize: '0.82rem', color: panelTheme.grayText, lineHeight: 1.55, marginBottom: '0.9rem' }}>
        sign in as this exact inbox — if another account appears, pick &ldquo;use another account&rdquo;.
      </div>
      <input
        type="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder="you@yourfirm.com"
        data-testid="email-intent-input"
        autoFocus
        style={{
          width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem',
          borderRadius: panelTheme.radiusInput, border: `1px solid ${panelTheme.grayBorder}`,
          fontSize: '0.9rem', color: panelTheme.ink, background: panelTheme.surface,
        }}
      />
      {firmApproved === true && (
        <div
          data-testid="email-intent-firm-approved"
          style={{
            marginTop: '0.65rem',
            fontSize: '0.78rem', color: panelTheme.successDeep, background: panelTheme.successBg,
            borderRadius: panelTheme.radiusChip, padding: '0.55rem 0.75rem', lineHeight: 1.55,
          }}
        >
          ✓ your firm has approved Prest0n — just sign in as this inbox.
        </div>
      )}
      {slug === 'outlook' && firmApproved !== true && (
        <div
          data-testid="email-intent-admin-warning"
          style={{
            marginTop: '0.65rem',
            fontSize: '0.78rem', color: panelTheme.goldInk, background: panelTheme.goldTint,
            borderRadius: panelTheme.radiusChip, padding: '0.55rem 0.75rem', lineHeight: 1.55,
          }}
        >
          if microsoft asks for approval: the admin approves it from their admin
          console — <strong>never</strong> by signing in themselves. you sign in as the inbox.
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', alignItems: 'center', marginTop: '1.1rem' }}>
        <button
          onClick={() => onContinue(null)}
          data-testid="email-intent-skip"
          style={{
            background: 'none', border: 'none', padding: '0.45rem 0.6rem',
            fontSize: '0.82rem', fontWeight: 600, color: panelTheme.grayText,
            cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          skip
        </button>
        <button
          onClick={submit}
          disabled={!valid}
          data-testid="email-intent-continue"
          style={{
            background: valid ? panelTheme.ink : panelTheme.grayBorder,
            border: '1px solid transparent',
            borderRadius: panelTheme.radiusPill, padding: '0.55rem 1.1rem',
            fontSize: '0.84rem', fontWeight: 700,
            color: valid ? '#fff' : panelTheme.grayText,
            cursor: valid ? 'pointer' : 'default',
          }}
        >
          continue to sign-in
        </button>
      </div>
    </PanelModal>
  );
};

export default EmailIntentPrompt;
